import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Grid,
    InputBaseComponentProps,
    Typography,
    Tooltip,
    Button,
} from '@mui/material';
import DefaultHypothesisValue from './DefaultHypothesisValue';
import HypothesisValue from './HypothesisValue';
import TotalCost from './TotalCost';
import HypothesisDate from './HypothesisDate';
import {
    SmicTotalData,
    SmicValuesPerYear,
} from '../../../GVTData/helpers/hooks/gvtData';
import InfoIcon from '@mui/icons-material/Info';
import SmicHypothesesModal from './components/SmicHypothesesModal';
import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { TRACKABLE_EVENTS, trackEventPRH } from 'helpers/trackerHelpers';
import Flex from 'ui/Flex/Flex';
import { useGetHrSimulationAffectedAgentsBySmic } from './helpers/hooks/smic';
import {
    INDICIAIRE_STATUSES,
    NON_INDICIAIRE_STATUSES,
} from './helpers/dataHelpers';
import { StyBlock } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/PayConstantsEntryForm/helpers/StyledComponents';

type SmicHypothesisEntryFormProps = {
    isLoading: boolean;
    hrSimulation: HrSimulation;
    title: string;
    isHrSimulationResponseFetching: boolean;
    defaultValue: number | undefined;
    value: number | undefined;
    date: string | undefined;
    onChange: (newValue: number) => void;
    onDateChange: (newDate: string) => void;
    inputProps: InputBaseComponentProps;
    totalCost: SmicTotalData;
    costPerYear: SmicValuesPerYear;
};

const HypothesisEntryForm = ({
    hrSimulation,
    title,
    defaultValue,
    value,
    date,
    onChange,
    onDateChange,
    inputProps,
    isHrSimulationResponseFetching,
    totalCost,
    costPerYear,
    isLoading = false,
}: SmicHypothesisEntryFormProps): React.ReactElement => {
    const { t } = useTranslation();
    const { data: smicHypothesesData, isLoading: isSmicHypothesesDataLoading } =
        useGetHrSimulationAffectedAgentsBySmic(hrSimulation.id);
    const smicHypotheses = smicHypothesesData || [];
    const hasSmicData =
        smicHypotheses.length > 0 && !isSmicHypothesesDataLoading;

    const [showModal, setShowModal] = useState(false);
    const [indiciaireMode, setIndiciaireMode] = useState(false);
    return (
        <StyBlock>
            <Typography variant="h4" sx={{ fontSize: '16px' }}>
                {t(title)}
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={4}>
                    <DefaultHypothesisValue
                        defaultValue={defaultValue}
                        typeValue="number"
                        isLoading={isLoading}
                    />
                </Grid>

                <Grid item xs={4}>
                    <HypothesisValue
                        futureValue={value}
                        typeValue="number"
                        onChange={onChange}
                        inputProps={inputProps}
                        isLoading={isLoading}
                    />
                </Grid>
                <Grid item xs={4}>
                    <HypothesisDate
                        applicationDate={date}
                        onDateChange={onDateChange}
                        isLoading={isLoading}
                    />
                </Grid>
            </Grid>
            <TotalCost
                costPerYear={costPerYear.perYearCost}
                totalCost={totalCost.totalCost}
                label={t('humanResources.simulation.smic.totalIncidence')}
                isLoading={isLoading || isHrSimulationResponseFetching}
            />
            <Flex justifyContent="space-between">
                <TotalCost
                    costPerYear={costPerYear.nonIndiciaireCost}
                    totalCost={totalCost.nonIndiciaireCost}
                    label={t('humanResources.simulation.smic.directIncidence')}
                    endAdornment={
                        <>
                            <Tooltip
                                title={t(
                                    'humanResources.simulation.smic.directIncidenceTooltip',
                                )}
                            >
                                <InfoIcon
                                    color="primary"
                                    sx={{ fontSize: 16 }}
                                />
                            </Tooltip>{' '}
                            {hasSmicData &&
                                smicHypotheses.some((hypothesis) =>
                                    NON_INDICIAIRE_STATUSES.includes(
                                        hypothesis.mantyStatus,
                                    ),
                                ) && (
                                    <Button
                                        size="small"
                                        onClick={() => {
                                            setIndiciaireMode(false);
                                            trackEventPRH(
                                                TRACKABLE_EVENTS.human_resources
                                                    .simulation.scenario
                                                    .smicDetail,
                                            );
                                            setShowModal(true);
                                        }}
                                        variant="text"
                                        sx={{
                                            fontSize: '12px',
                                            flexShrink: '0',
                                        }}
                                    >
                                        {t(
                                            'humanResources.simulation.agentsDetail',
                                        )}
                                    </Button>
                                )}
                        </>
                    }
                    isLoading={isLoading || isHrSimulationResponseFetching}
                />

                <TotalCost
                    costPerYear={costPerYear.indiciaireCost}
                    totalCost={totalCost.indiciaireCost}
                    label={t(
                        'humanResources.simulation.smic.indirectIncidence',
                    )}
                    endAdornment={
                        <>
                            {' '}
                            <Tooltip
                                title={t(
                                    'humanResources.simulation.smic.indirectIncidenceTooltip',
                                )}
                                key="button-element"
                            >
                                <InfoIcon
                                    color="primary"
                                    sx={{ fontSize: 16 }}
                                />
                            </Tooltip>{' '}
                            {hasSmicData &&
                                smicHypotheses.some((hypothesis) =>
                                    INDICIAIRE_STATUSES.includes(
                                        hypothesis.mantyStatus,
                                    ),
                                ) && (
                                    <Button
                                        size="small"
                                        onClick={() => {
                                            setIndiciaireMode(true);
                                            trackEventPRH(
                                                TRACKABLE_EVENTS.human_resources
                                                    .simulation.scenario
                                                    .smicDetail,
                                            );
                                            setShowModal(true);
                                        }}
                                        variant="text"
                                        sx={{
                                            fontSize: '12px',
                                            flexShrink: '0',
                                        }}
                                    >
                                        {t(
                                            'humanResources.simulation.agentsDetail',
                                        )}
                                    </Button>
                                )}
                        </>
                    }
                    isLoading={isLoading || isHrSimulationResponseFetching}
                />
            </Flex>

            {showModal && (
                <SmicHypothesesModal
                    hrSimulation={hrSimulation}
                    smicHypotheses={smicHypotheses}
                    isSmicHypothesesLoading={isSmicHypothesesDataLoading}
                    showModal={showModal}
                    indiciaire={indiciaireMode}
                    setShowModal={() => setShowModal(false)}
                />
            )}
        </StyBlock>
    );
};

export default memo(HypothesisEntryForm);
